import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { usersAPI } from '../utils/api';
import { toast } from 'sonner';
import { Download, Upload, Loader2, CheckCircle2, XCircle, FileSpreadsheet } from 'lucide-react';

const ImportUsersDialog = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {inserted, ids} | null
  const [errors, setErrors] = useState(null); // [{row, field, message}] | null
  const [serverError, setServerError] = useState(null); // string | null
  const fileInputRef = useRef(null);

  const canClose = !loading;

  const handleClose = () => {
    if (!canClose) return;
    // Reset state
    setFile(null);
    setLoading(false);
    setResult(null);
    setErrors(null);
    setServerError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose(!!result);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await usersAPI.importTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_usuarios.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo descargar la plantilla');
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('El archivo debe tener extensión .xlsx');
      e.target.value = '';
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error('El archivo excede los 2MB permitidos');
      e.target.value = '';
      return;
    }
    setFile(f);
    setResult(null);
    setErrors(null);
    setServerError(null);
  };

  const handleImport = async () => {
    if (!file || loading) return;
    setLoading(true);
    setResult(null);
    setErrors(null);
    setServerError(null);
    try {
      const { data } = await usersAPI.importUsers(file);
      setResult(data);
      toast.success(`${data.inserted} usuarios importados correctamente`);
    } catch (err) {
      const payload = err.response?.data;
      if (Array.isArray(payload?.errors)) {
        setErrors(payload.errors);
        toast.error(payload.detail || 'La importación fue cancelada por errores en el archivo');
      } else {
        const msg = typeof payload?.detail === 'string' ? payload.detail : 'Error al importar usuarios';
        setServerError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setResult(null);
    setErrors(null);
    setServerError(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => { if (loading) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
        data-testid="import-users-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-bold text-[#2D2D2D]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Importar Usuarios
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5A5A5A]">
            Crea múltiples usuarios de una vez usando una plantilla Excel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Instrucciones */}
          <div className="p-4 bg-[#fbf7f0] border border-[#e8dfd0] rounded-lg text-sm text-[#5A5A5A] space-y-2">
            <p className="font-medium text-[#2D2D2D]">Antes de importar:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Descarga la plantilla y complétala respetando los encabezados.</li>
              <li>
                Campos obligatorios: <strong>Nombre Completo</strong>, <strong>Correo Electrónico</strong>,
                {' '}<strong>Rol</strong> (nombre exacto), <strong>Contraseña</strong>.
              </li>
              <li>Los usuarios se crearán con la opción <em>“Solicitar cambio de contraseña”</em> activada.</li>
              <li>Si CUALQUIER fila falla la validación, no se importará ningún registro.</li>
            </ul>
          </div>

          {/* Descargar plantilla */}
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadTemplate}
            disabled={loading}
            className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white"
            data-testid="import-users-download-template"
          >
            <Download size={16} className="mr-2" />
            Descargar plantilla
          </Button>

          {/* Selección archivo */}
          {!result && !errors && !serverError && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2D2D]">Archivo (.xlsx)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                disabled={loading}
                className="block w-full text-sm text-[#5A5A5A] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#9d8272] file:text-white hover:file:bg-[#8a7162] disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="import-users-file-input"
              />
              {file && (
                <p className="text-xs text-[#5A5A5A] flex items-center gap-1">
                  <FileSpreadsheet size={14} />
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>
          )}

          {/* Spinner overlay */}
          {loading && (
            <div className="p-6 bg-white border border-[#E8E8E7] rounded-lg flex items-center justify-center gap-3" data-testid="import-users-loading">
              <Loader2 className="animate-spin text-[#9d8272]" size={24} />
              <span className="text-sm text-[#5A5A5A]">Importando usuarios, por favor espere…</span>
            </div>
          )}

          {/* Resultado éxito */}
          {result && !loading && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3" data-testid="import-users-success">
              <CheckCircle2 className="text-green-600 mt-0.5" size={22} />
              <div className="flex-1">
                <p className="font-medium text-green-800">
                  {result.inserted} usuario{result.inserted === 1 ? '' : 's'} creado{result.inserted === 1 ? '' : 's'} correctamente
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Todos con “Solicitar cambio de contraseña en el próximo inicio de sesión” activado.
                </p>
              </div>
            </div>
          )}

          {/* Errores de validación */}
          {errors && !loading && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-2" data-testid="import-users-errors">
              <div className="flex items-start gap-2">
                <XCircle className="text-red-600 mt-0.5" size={20} />
                <p className="font-medium text-red-800">
                  Importación cancelada. Se encontraron {errors.length} error{errors.length === 1 ? '' : 'es'}:
                </p>
              </div>
              <div className="max-h-64 overflow-y-auto border border-red-200 rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-red-100 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-semibold text-red-900">Fila</th>
                      <th className="p-2 text-left font-semibold text-red-900">Campo</th>
                      <th className="p-2 text-left font-semibold text-red-900">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e, i) => (
                      <tr key={i} className="border-t border-red-100">
                        <td className="p-2 text-red-800 font-mono">{e.row}</td>
                        <td className="p-2 text-red-800">{e.field}</td>
                        <td className="p-2 text-red-700">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Error servidor */}
          {serverError && !loading && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="import-users-server-error">
              <XCircle className="text-red-600 mt-0.5" size={20} />
              <p className="text-sm text-red-800">{serverError}</p>
            </div>
          )}
        </div>

        {/* Footer sticky */}
        <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-white border-t flex justify-end gap-2 mt-4">
          {(errors || serverError) && !loading && (
            <Button type="button" variant="outline" onClick={handleRetry} data-testid="import-users-retry">
              Volver a intentar
            </Button>
          )}
          {!result && !errors && !serverError && (
            <Button
              type="button"
              onClick={handleImport}
              disabled={!file || loading}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
              data-testid="import-users-submit"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
              {loading ? 'Importando…' : 'Importar'}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            data-testid="import-users-close"
          >
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportUsersDialog;
